export type TranscriptionUrlSource =
  | 'youtube'
  | 'instagram'
  | 'tiktok'
  | 'googledrive'
  | 'importurl';

export type TranscriptionUrlDetection =
  | {
      ok: true;
      source: TranscriptionUrlSource;
      url: string;
    }
  | {
      ok: false;
      error: string;
    };

function isHost(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function youtubeVideoId(parsed: URL): string | null {
  const hostname = parsed.hostname.toLowerCase();
  let candidate: string | null = null;

  if (isHost(hostname, 'youtu.be')) {
    candidate = parsed.pathname.split('/').filter(Boolean)[0] || null;
  } else {
    candidate = parsed.searchParams.get('v');
    if (!candidate) {
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (['shorts', 'live', 'embed', 'v'].includes(parts[0])) {
        candidate = parts[1] || null;
      }
    }
  }

  return candidate && /^[A-Za-z0-9_-]{11}$/.test(candidate) ? candidate : null;
}

/**
 * Normalize an open transcription URL and determine which importer owns it.
 *
 * The result is intentionally based on the parsed hostname rather than the
 * source selected by a client. This keeps web, mobile, API clients, retries,
 * and old saved drafts consistent and avoids false matches such as
 * `youtube.com.example.com`.
 */
export function detectTranscriptionUrl(
  rawUrl: unknown,
): TranscriptionUrlDetection {
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) {
    return { ok: false, error: 'Enter a link to transcribe.' };
  }

  const trimmed = rawUrl.trim();
  // Chrome's URL parser escapes spaces in a host ("not a link" parses as
  // https://not%20a%20link/), so plain text would pass as a link. Spaces
  // later in the link (a file name) are fine.
  const hostPart = trimmed.replace(/^[a-z][a-z\d+.-]*:\/\/|^\/\//i, '');
  if (/\s/.test(hostPart.split(/[/?#]/)[0])) {
    return {
      ok: false,
      error: 'Enter a complete public link, such as https://example.com/file.',
    };
  }
  const candidate = trimmed.startsWith('//')
    ? `https:${trimmed}`
    : /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return {
      ok: false,
      error: 'Enter a complete public link, such as https://example.com/file.',
    };
  }

  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return {
      ok: false,
      error: 'Only public HTTP or HTTPS links are supported.',
    };
  }
  if (parsed.username || parsed.password) {
    return {
      ok: false,
      error: 'Links containing a username or password are not supported.',
    };
  }

  const hostname = parsed.hostname.toLowerCase().replace(/\.$/, '');
  // A public host has a dot ("hello" would become https://hello/).
  if (!hostname || !hostname.includes('.')) {
    return { ok: false, error: 'Enter a valid public link.' };
  }
  if (hostname === 'localhost' || hostname.endsWith('.local')) {
    return { ok: false, error: 'The media link must be publicly accessible.' };
  }

  if (
    isHost(hostname, 'youtube.com') ||
    isHost(hostname, 'youtu.be') ||
    isHost(hostname, 'youtube-nocookie.com')
  ) {
    const videoId = youtubeVideoId(parsed);
    if (!videoId) {
      return {
        ok: false,
        error:
          'This YouTube link does not contain a video. Paste a watch, Shorts, Live, embed, or youtu.be link.',
      };
    }
    return {
      ok: true,
      source: 'youtube',
      // All downstream YouTube integrations understand this canonical form.
      url: `https://www.youtube.com/watch?v=${videoId}`,
    };
  }

  if (isHost(hostname, 'instagram.com')) {
    if (!/^\/(p|reel|reels|tv)\/[^/]+/i.test(parsed.pathname)) {
      return {
        ok: false,
        error: 'Paste a public Instagram post, reel, or video link.',
      };
    }
    return { ok: true, source: 'instagram', url: parsed.toString() };
  }

  if (isHost(hostname, 'tiktok.com')) {
    return { ok: true, source: 'tiktok', url: parsed.toString() };
  }

  if (hostname === 'drive.google.com') {
    const hasFileId =
      /\/file\/(?:u\/\d+\/)?d\/[A-Za-z0-9_-]+/.test(parsed.pathname) ||
      /^[A-Za-z0-9_-]+$/.test(parsed.searchParams.get('id') || '');
    if (!hasFileId) {
      return {
        ok: false,
        error: 'Paste a public Google Drive file link, not a folder link.',
      };
    }
    return { ok: true, source: 'googledrive', url: parsed.toString() };
  }

  return { ok: true, source: 'importurl', url: parsed.toString() };
}
